import { useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_EMAIL = "info.nomadroadmap@gmail.com";

// Reads the signed-in user (if any) so the form is pre-filled and the ticket
// is linked to their account. Same storage shape api.js uses.
const storedUser = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}") || {}; } catch (e) { return {}; }
};

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const user = storedUser();
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [message, setMessage] = useState("");
  const [state, setState] = useState({ sending: false, done: false, error: "" });

  const canSend = message.trim().length > 0 && /\S+@\S+\.\S+/.test(email) && !state.sending;

  const handleSend = async () => {
    if (!canSend) return;
    setState({ sending: true, done: false, error: "" });
    try {
      const headers = { "Content-Type": "application/json" };
      if (user.token) headers.Authorization = `Bearer ${user.token}`;
      const res = await fetch(`${API_URL}/api/support/contact`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: name.trim(), email: email.trim(), subject: "Support request", message: message.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not send your message");
      setState({ sending: false, done: true, error: "" });
      setMessage("");
    } catch (e) {
      setState({ sending: false, done: false, error: e.message || "Could not send your message" });
    }
  };

  const inputCls = "w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          aria-label="Contact support"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:scale-105 transition-all"
        >
          <MessageCircle />
        </button>
      )}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl border bg-background flex flex-col overflow-hidden animate-fade-in">
          <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Nomad Support</h3>
              <p className="text-xs opacity-80">We reply by email, usually within 24 hours.</p>
            </div>
            <button onClick={() => setIsOpen(false)} aria-label="Close"><X className="h-5 w-5" /></button>
          </div>

          <div className="p-4 space-y-3">
            {state.done ? (
              <div className="text-sm space-y-2">
                <p className="font-medium">Got it - thanks!</p>
                <p>We'll reply to <span className="font-medium">{email}</span> within 24 hours.</p>
                <p className="text-muted-foreground text-xs">
                  Need to add something? Email <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </p>
                <Button variant="outline" size="sm" onClick={() => setState({ sending: false, done: false, error: "" })}>Send another message</Button>
              </div>
            ) : (
              <>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email (so we can reply)" className={inputCls} />
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  rows={5}
                  className={`${inputCls} resize-none`}
                />
                {state.error && <p className="text-xs text-red-600">{state.error} - or email us at <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>}
                <div className="flex items-center justify-between gap-2">
                  <a className="text-xs text-muted-foreground underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                  <Button size="sm" onClick={handleSend} disabled={!canSend}>
                    <Send className="h-4 w-4 mr-1" /> {state.sending ? "Sending..." : "Send"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
